import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Search } from '@mui/icons-material';
import CustomPagination from '../../components/common/CustomPagination';
import { fetchAffiliateRankings } from '../../services/affiliate';

const formatCurrency = (value) => `₦${Number(value || 0).toLocaleString('en-NG', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const formatRmb = (value) => `¥${Number(value || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const AffiliateRankingsPage = () => {
  const { t } = useTranslation(['affiliate', 'common']);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('monthly');
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  const debouncedSearch = useDebounce(filters.search, 500);
  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  const loadRankings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchAffiliateRankings({
        period,
        page: page + 1,
        page_size: pageSize,
        search: debouncedSearch || undefined,
        status: filters.status || undefined,
      });
      setRows(response.results || []);
      setSummary(response.summary || {});
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Failed to fetch affiliate rankings:', error);
      setRows([]);
      setSummary({});
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [period, page, pageSize, debouncedSearch, filters.status]);

  useEffect(() => {
    loadRankings();
  }, [loadRankings]);

  const handlePeriodChange = (nextPeriod) => {
    setPeriod(nextPeriod);
    setPage(0);
  };

  const handlePageChange = (newPage) => {
    setPage(Math.max(0, Math.min(newPage, totalPages - 1)));
  };

  const handleRowsPerPageChange = (event) => {
    const value = event?.target?.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      return;
    }

    const numericValue = Number(value);
    if (!Number.isNaN(numericValue) && numericValue > 0) {
      setPageSize(numericValue);
      setPage(0);
    }
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput, 10);
    if (value && value > 0 && value <= 1000) {
      setPageSize(value);
      setPage(0);
    }
    setCustomRowsInput('');
    setCustomRowsActive(false);
  };

  const columns = [
    {
      field: 'rank',
      headerName: t('affiliate:rankings.table.rank'),
      width: 90,
      sortable: false,
    },
    {
      field: 'name',
      headerName: t('affiliate:rankings.table.affiliate'),
      minWidth: 240,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {params.row.name || params.row.username || '-'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {params.row.email || params.row.phone || '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'affiliate_code',
      headerName: t('affiliate:rankings.table.referralCode'),
      width: 150,
    },
    {
      field: 'monthly_earning',
      headerName: t('affiliate:rankings.table.monthlyEarning'),
      width: 190,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2" fontWeight={600}>{formatCurrency(params.value)}</Typography>
          <Typography variant="caption" color="text.secondary">{formatRmb(params.row.monthly_earning_rmb)}</Typography>
        </Box>
      ),
    },
    {
      field: 'all_time_earning',
      headerName: t('affiliate:rankings.table.allTimeEarning'),
      width: 190,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2" fontWeight={600}>{formatCurrency(params.value)}</Typography>
          <Typography variant="caption" color="text.secondary">{formatRmb(params.row.all_time_earning_rmb)}</Typography>
        </Box>
      ),
    },
    {
      field: 'monthly_rewards',
      headerName: t('affiliate:rankings.table.monthlyRewards'),
      width: 150,
    },
    {
      field: 'all_time_rewards',
      headerName: t('affiliate:rankings.table.allTimeRewards'),
      width: 150,
    },
    {
      field: 'total_referrals',
      headerName: t('affiliate:rankings.table.referrals'),
      width: 130,
    },
    {
      field: 'status',
      headerName: t('affiliate:rankings.table.status'),
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          color={params.value === 'active' ? 'success' : 'default'}
          label={params.value === 'active' ? t('affiliate:status.active') : t('affiliate:status.inactive')}
        />
      ),
    },
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden',
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
          {t('affiliate:rankings.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('affiliate:rankings.description')}
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">{t('affiliate:rankings.summary.totalAffiliates')}</Typography>
              <Typography variant="h5" fontWeight={700}>{summary.total_affiliates || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">{t('affiliate:rankings.summary.monthlyEarning')}</Typography>
              <Typography variant="h5" fontWeight={700} color="primary">{formatCurrency(summary.monthly_earning)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">{t('affiliate:rankings.summary.allTimeEarning')}</Typography>
              <Typography variant="h5" fontWeight={700} color="primary">{formatCurrency(summary.all_time_earning)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant={period === 'monthly' ? 'contained' : 'outlined'}
                  onClick={() => handlePeriodChange('monthly')}
                >
                  {t('affiliate:rankings.period.monthly')}
                </Button>
                <Button
                  variant={period === 'all_time' ? 'contained' : 'outlined'}
                  onClick={() => handlePeriodChange('all_time')}
                >
                  {t('affiliate:rankings.period.allTime')}
                </Button>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                size="small"
                fullWidth
                label={t('affiliate:rankings.filters.search')}
                value={filters.search}
                onChange={(event) => {
                  setFilters((previous) => ({ ...previous, search: event.target.value }));
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('affiliate:rankings.filters.status')}</InputLabel>
                <Select
                  value={filters.status}
                  label={t('affiliate:rankings.filters.status')}
                  onChange={(event) => {
                    setFilters((previous) => ({ ...previous, status: event.target.value }));
                    setPage(0);
                  }}
                >
                  <MenuItem value="">{t('affiliate:rankings.filters.allStatuses')}</MenuItem>
                  <MenuItem value="active">{t('affiliate:status.active')}</MenuItem>
                  <MenuItem value="inactive">{t('affiliate:status.inactive')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadRankings}
                disabled={loading}
              >
                {t('common:refresh')}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ height: 650, width: '100%', overflowX: 'auto' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.partner_id}
              loading={loading}
              hideFooter
              paginationMode="server"
              rowCount={totalCount}
              page={page}
              pageSize={pageSize}
              disableSelectionOnClick
              sx={{
                border: 'none',
                minWidth: 1120,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: 'primary.main',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'primary.contrastText',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'primary.contrastText',
                },
                '& .MuiDataGrid-columnSeparator': {
                  color: 'primary.contrastText',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover',
                },
                '& .MuiDataGrid-cell': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  py: 1,
                },
              }}
            />
          </Box>
          <Box sx={{ px: 2, pb: 2 }}>
            <CustomPagination
              page={page}
              totalPages={totalPages}
              rowsPerPage={pageSize}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              customRowsActive={customRowsActive}
              customRowsInput={customRowsInput}
              onCustomRowsInputChange={(event) => setCustomRowsInput(event.target.value)}
              onCustomRowsInputBlur={handleCustomRowsInputBlur}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AffiliateRankingsPage;
